import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Check if user is logged in on app start
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await authAPI.getProfile();
        // Backend now returns { user } directly for profile endpoint
        if (response.user) {
          setUser(response.user);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  // Shared by signUp (when it returns a session) and verifyEmail.
  const adoptSession = (response) => {
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
    setUser(response.user);
  };

  const signUp = async (userData) => {
    try {
      setError('');
      setLoading(true);
      const response = await authAPI.signUp(userData);

      // Signup no longer hands out a session — the account is inert until the
      // emailed link is clicked. Report that instead of pretending to log in.
      if (response.verificationRequired) {
        return { success: true, verificationRequired: true, email: response.email };
      }

      adoptSession(response);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Sign up failed';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Consuming the emailed token both verifies and signs in.
  const verifyEmail = async (token) => {
    try {
      setError('');
      const response = await authAPI.verifyEmail(token);
      adoptSession(response);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        code: error.response?.data?.code,
        message: error.response?.data?.error || 'Could not confirm your email.'
      };
    }
  };

  const signIn = async (credentials) => {
    try {
      setError('');
      setLoading(true);
      const response = await authAPI.signIn(credentials);
      
      // Backend now returns { token, user } directly
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Sign in failed';
      setError(errorMessage);
      // The caller needs the code to offer a "resend confirmation" action
      // rather than just showing the text.
      return { success: false, code: error.response?.data?.code, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setError('');
  };

  const clearError = () => {
    setError('');
  };

  const value = {
    user,
    loading,
    error,
    signUp,
    verifyEmail,
    signIn,
    signOut,
    clearError,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
